/**
 * Multi-select Browse/Swap filter helpers (request shape + result grouping).
 *
 * The backend accepts the new soft multi-select filter fields (filter_tags,
 * filter_cuisines, filter_protein_types, filter_cookware,
 * filter_cook_time_buckets, filter_dish_types). OR within a category, strongest matches (all
 * selected categories satisfied) first, partial matches afterward — nothing
 * is hard-removed, so useful meals are never dead-ended behind a
 * "No meals match your filters" screen.
 */
import { isServerRanked } from './mealSort';

/** Build the /browse-meals/macro-aware request payload for the filter state. */
export function buildBrowseFilterParams(sel) {
  sel = sel || {};
  var params = {};
  if (sel.tags && sel.tags.length) params.filter_tags = sel.tags.slice();
  if (sel.cuisines && sel.cuisines.length) params.filter_cuisines = sel.cuisines.slice();
  if (sel.proteins && sel.proteins.length) params.filter_protein_types = sel.proteins.slice();
  if (sel.cookware && sel.cookware.length) params.filter_cookware = sel.cookware.slice();
  if (sel.cookTimes && sel.cookTimes.length) params.filter_cook_time_buckets = sel.cookTimes.slice();
  if (sel.dishTypes && sel.dishTypes.length) params.filter_dish_types = sel.dishTypes.slice();
  return params;
}

/** Total number of individual selections across every filter category. */
export function countActiveFilters(sel) {
  sel = sel || {};
  return (sel.tags ? sel.tags.length : 0) +
    (sel.cuisines ? sel.cuisines.length : 0) +
    (sel.proteins ? sel.proteins.length : 0) +
    (sel.cookware ? sel.cookware.length : 0) +
    (sel.cookTimes ? sel.cookTimes.length : 0) +
    (sel.dishTypes ? sel.dishTypes.length : 0);
}

export function emptyBrowseFilters() {
  return { tags: [], cuisines: [], proteins: [], cookware: [], cookTimes: [], dishTypes: [] };
}

/** Seed only real, persisted profile preferences into a new local session. */
export function browseFiltersFromProfile(profile) {
  var filters = emptyBrowseFilters();
  if (!profile) return filters;
  filters.cookware = Array.isArray(profile.cookware) ? profile.cookware.slice() : [];
  // Browse's 60 bucket means "60+ minutes", not "up to 60 minutes". Only
  // saved maxima that faithfully match cumulative short buckets may seed.
  var supportedTimes = [15, 30, 45];
  var maxCookTime = Number(profile.max_cook_time);
  if (supportedTimes.indexOf(maxCookTime) !== -1) filters.cookTimes = [maxCookTime];
  return filters;
}

/** Search every customer-facing catalog option category, including Dish Type. */
export function filterBrowseOptions(options, query) {
  options = options || {};
  var q = String(query || '').trim().toLowerCase();
  function match(list) {
    if (!q) return (list || []).slice();
    return (list || []).filter(function (value) {
      return String(value).toLowerCase().indexOf(q) !== -1;
    });
  }
  return {
    tags: match(options.tags),
    cuisines: match(options.cuisines),
    proteins: match(options.proteins),
    cookware: match(options.cookware),
    dishTypes: match(options.dishTypes),
  };
}

export function hasAnyFilter(sel) {
  return countActiveFilters(sel) > 0;
}

/**
 * Split an already-sorted meal list into "Best matches" (every selected
 * filter category satisfied) and "Other meals" (partial), preserving the
 * incoming order within each group. When no filters were applied the list is
 * returned unsectioned.
 *
 * A server-ranked list (Phase 2B `server_rank`) is never reordered: the
 * backend already ranks full matches first WITHIN each nutrition group (every
 * acceptable card before any Out of Range one), so "Best matches" is the
 * leading run of full matches and "Other meals" is everything after it, in
 * server order — an Out of Range full match stays below the acceptable cards.
 */
export function splitFilterMatches(meals) {
  var list = meals || [];
  var sectioned = list.some(function (m) {
    return m && (m.filter_categories_selected || 0) > 0;
  });
  if (!sectioned) {
    return { sectioned: false, best: list.slice(), other: [] };
  }
  if (isServerRanked(list)) {
    var lead = 0;
    while (lead < list.length && list[lead] && list[lead].filter_full_match) lead += 1;
    return { sectioned: true, best: list.slice(0, lead), other: list.slice(lead) };
  }
  var best = [];
  var other = [];
  list.forEach(function (m) {
    if (m && m.filter_full_match) best.push(m);
    else other.push(m);
  });
  return { sectioned: true, best: best, other: other };
}
